import {getGraphql} from '@nlabs/metropolisjs/utils';
import type {FluxFramework} from '@nlabs/arkhamjs';

import type {WeatherDay, WeatherSnapshot} from '@chicago-sentinel/contracts/weather';

export const CHICAGO_WEATHER_QUERY = `
  query ChicagoWeather($provider: String) {
    chicagoWeather(provider: $provider) {
      apparentTemperatureF
      currentTemperatureF
      forecast {
        date
        highF
        lowF
        precipitationChance
        summary
        weatherCode
      }
      location
      observedAt
      precipitationChance
      sourceName
      sourceUrl
      stale
      summary
      updatedAt
      weatherCode
      windDirection
      windSpeedMph
    }
  }
`;

export const weatherGraphqlEndpoint = (hostname: string): string => {
  if(hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:3401/public';
  const stage = hostname.startsWith('local.') || hostname.startsWith('dev.') ? 'dev' : 'prod';
  return `https://api.chicagosentineltimes.com/${stage}/public`;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const isWeatherDay = (value: unknown): value is WeatherDay =>
  isRecord(value) &&
  typeof value.date === 'string' &&
  isNumber(value.highF) &&
  isNumber(value.lowF) &&
  isNumber(value.precipitationChance) &&
  typeof value.summary === 'string' &&
  isNumber(value.weatherCode);

const isWeatherSnapshot = (value: unknown): value is WeatherSnapshot =>
  isRecord(value) &&
  isNumber(value.apparentTemperatureF) &&
  isNumber(value.currentTemperatureF) &&
  Array.isArray(value.forecast) &&
  value.forecast.every(isWeatherDay) &&
  value.location === 'Chicago' &&
  typeof value.observedAt === 'string' &&
  isNumber(value.precipitationChance) &&
  value.sourceName === 'National Weather Service' &&
  value.sourceUrl === 'https://www.weather.gov/' &&
  typeof value.stale === 'boolean' &&
  typeof value.summary === 'string' &&
  typeof value.updatedAt === 'string' &&
  isNumber(value.weatherCode) &&
  typeof value.windDirection === 'string' &&
  isNumber(value.windSpeedMph);

export const parseChicagoWeatherData = (value: unknown): WeatherSnapshot => {
  const weather = isRecord(value) ? value.chicagoWeather : undefined;
  if(!isWeatherSnapshot(weather)) throw new Error('The Chicago weather service returned an invalid response.');
  return weather;
};

export const loadChicagoWeather = async (flux: FluxFramework, hostname: string): Promise<WeatherSnapshot> => {
  const data = await getGraphql(
    flux,
    weatherGraphqlEndpoint(hostname),
    false,
    {
      query: CHICAGO_WEATHER_QUERY,
      variables: {provider: 'nws'}
    },
    {}
  );
  return parseChicagoWeatherData(data);
};
